<template>
  <v-card>
    <v-card-text class="d-flex align-center ga-4 border-b bg-grey-lighten-5">
      <v-btn border color="white" @click="$router.back()">
        <v-icon icon="mdi-chevron-left"></v-icon>
        <span class="font-weight-medium">Назад</span>
      </v-btn>
      <v-sheet
        class="d-flex align-center justify-center flex-shrink-0"
        color="light-blue"
        height="36"
        rounded="lg"
        width="36">
        <v-icon
          :color="selectedClassColor"
          :icon="selectedClassIcon"
          size="18"></v-icon>
      </v-sheet>
      <div>
        <div class="font-size-11 font-weight-bold text-disabled text-uppercase">
          Пошук в класі
        </div>
        <div class="font-size-14 font-weight-bold">
          {{ selectedClass?.name }}
        </div>
      </div>
    </v-card-text>

    <v-card-text v-if="savedConfigs.length" class="border-b bg-grey-lighten-5">
      <div class="d-flex flex-wrap align-center ga-3">
        <div class="d-flex align-center ga-2">
          <v-icon
            color="secondary"
            icon="mdi-bookmark-outline"
            size="small"></v-icon>
          <span
            class="font-size-11 font-weight-bold text-disabled text-uppercase">
            Збережені конфігурації:
          </span>
        </div>

        <v-chip
          v-for="config in savedConfigs"
          :key="config.id"
          class="font-size-13"
          closable
          color="secondary"
          border
          @click="applyAttributeConfig(config)"
          @click:close.stop="removeAttributeConfig(config.id)">
          <v-icon class="mr-2" icon="mdi-check" size="small"></v-icon>
          {{ config.name }}
        </v-chip>
      </div>
    </v-card-text>

    <v-card-text>
      <div class="d-flex align-center ga-2 font-size-11 text-disabled mb-5">
        <v-icon icon="mdi-information-outline" size="x-small"></v-icon>
        Заповніть лише ті атрибути, за якими необхідно провести пошук
      </div>

      <v-row>
        <v-col cols="12" md="6">
          <b-select
            v-model="docTypeDefinitionId"
            :items="docTypeDefinitions"
            clearable
            hide-details
            label="Тип внутрішнього документа"
            @focus="$directory.fill('docTypeDefinitions')"></b-select>
        </v-col>
        <v-col cols="12" md="6">
          <b-select
            v-model="form.registrationJournalId"
            :disabled="!form.docTypeDefinitionId"
            :items="availableRegistrationJournals"
            :loading="$loading.isLoading('registrationJournalsByDefinition')"
            clearable
            hide-details
            label="Журнал реєстрації"></b-select>
        </v-col>
        <v-col cols="12" md="6">
          <b-input
            v-model="form.title"
            hide-details
            label="Назва / тема документа"
            placeholder="Наприклад: затвердження бюджету"></b-input>
        </v-col>
        <v-col cols="12" md="6">
          <b-select
            v-model="form.departmentId"
            :items="$directory.get('internalDepartments')"
            clearable
            hide-details
            label="Підрозділ-ініціатор"
            @focus="$directory.fill('internalDepartments')"></b-select>
        </v-col>
        <v-col cols="12" md="6">
          <b-input-date
            v-model="form.registrationDateFrom"
            hide-details
            label="Дата реєстрації — від"
            picker></b-input-date>
        </v-col>
        <v-col cols="12" md="6">
          <b-input-date
            v-model="form.registrationDateTo"
            hide-details
            label="Дата реєстрації — до"
            picker></b-input-date>
        </v-col>
      </v-row>

      <v-row v-if="form.fields.length">
        <v-col
          v-for="field in form.fields"
          :key="field.fieldDefinitionId"
          cols="6">
          <div>
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="d-flex align-center ga-1">
                <v-icon
                  color="secondary"
                  icon="mdi-plus"
                  size="14"></v-icon>
                <span class="label mb-0">
                  {{ field.label }}
                </span>
              </div>
              <div
                class="text-error d-flex align-center ga-1 cursor-pointer"
                @click="removeAttributeField(field.fieldDefinitionId)">
                <v-icon size="small">mdi-close</v-icon>
                <span class="font-size-11">Прибрати</span>
              </div>
            </div>
            <b-input
              v-if="field.fieldType === 'string'"
              v-model="field.value"
              :placeholder="getFieldPlaceholder(field.label)"
              hide-details>
            </b-input>
            <b-input-number
              v-else-if="field.fieldType === 'integer'"
              v-model="field.value"
              :placeholder="field.label"
              hide-details></b-input-number>
            <b-input-number
              v-else-if="field.fieldType === 'decimal'"
              v-model="field.value"
              :placeholder="field.label"
              :step="0.01"
              hide-details></b-input-number>
            <b-textarea
              v-else-if="field.fieldType === 'text'"
              v-model="field.value"
              :placeholder="field.label"
              hide-details
              rows="1"></b-textarea>
            <b-input-date
              v-else-if="field.fieldType === 'date'"
              v-model="field.value"
              hide-details
              picker></b-input-date>
            <div
              v-else-if="
                isSelectField(field.fieldType) &&
                !form.docTypeDefinitionId &&
                !field.entitySource &&
                !field.optionsJson?.length
              "
              class="d-flex align-center ga-1 text-disabled font-size-12 py-2">
              <v-icon icon="mdi-information-outline" size="x-small"></v-icon>
              <span>Оберіть тип документа для фільтрації по цьому полю</span>
            </div>
            <b-autocomplete
              v-else-if="isSelectField(field.fieldType)"
              v-model="field.value"
              :items="
                field.entitySource
                  ? $directory.get(field.entitySource as any)
                  : field.optionsJson || []
              "
              placeholder="Оберіть зі списку"
              :multiple="field.fieldType === 'multiselect'"
              chips
              hide-details
              @focus="
                field.entitySource && $directory.fill(field.entitySource as any)
              "></b-autocomplete>
            <b-checkbox
              v-else-if="field.fieldType === 'boolean'"
              v-model="field.value"
              :label="field.label"
              color="secondary"
              hide-details></b-checkbox>
            <b-input
              v-else
              v-model="field.value"
              :placeholder="field.label"
              hide-details></b-input>
          </div>
        </v-col>
      </v-row>

      <v-menu
        v-model="fieldMenu"
        :close-on-content-click="false"
        location="bottom"
        offset="6">
        <template #activator="{ props }">
          <v-btn
            class="mt-5 border border-dashed"
            border
            color="white"
            v-bind="props"
            @click="fillSearchDictionaries">
            <div
              class="d-flex align-center ga-1 font-weight-light text-secondary">
              <v-icon color="secondary">mdi-plus</v-icon>
              <span>Додати поле для пошуку</span>
              <v-chip
                class="ml-2"
                color="secondary">
                <span class="font-weight-bold">
                  {{ availableAttributeFields.length }}
                </span>
              </v-chip>
            </div>
          </v-btn>
        </template>

        <v-card
          :loading="$loading.isLoading('fieldDefinitions')"
          class="overflow-hidden"
          min-width="420">
          <v-card-title
            class="bg-grey-lighten-5 text-uppercase text-disabled font-size-11 font-weight-bold">
            Доступні атрибути
          </v-card-title>
          <v-divider></v-divider>
          <v-list
            class="overflow-y-auto pa-0"
            density="compact"
            max-height="360">
            <v-list-item
              v-for="field in availableAttributeFields"
              :key="getAvailableFieldKey(field)"
              class="border-b"
              min-height="40"
              @click="handleAddField(field)">
              <template #prepend>
                <v-icon
                  color="secondary"
                  icon="mdi-plus"
                  size="small"></v-icon>
              </template>
              <v-list-item-title class="font-size-13">
                {{ getAvailableFieldLabel(field) }}
              </v-list-item-title>
              <template #append>
                <v-chip
                  :class="getFieldTypeTextClass(getAvailableFieldType(field))"
                  :color="getFieldTypeColor(getAvailableFieldType(field))"
                  density="comfortable"
                  size="small">
                  <span class="font-size-11 font-weight-bold text-uppercase">
                    {{ getFieldTypeLabel(getAvailableFieldType(field)) }}
                  </span>
                </v-chip>
              </template>
            </v-list-item>
            <v-list-item v-if="!availableAttributeFields.length">
              <v-list-item-title class="text-disabled">
                Нічого не знайдено
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>

      <v-divider class="my-6"></v-divider>

      <div class="d-flex flex-wrap ga-3">
        <v-btn
          :loading="$loading.isLoading('edsSearchAttribute')"
          prepend-icon="mdi-magnify"
          @click="handleSearch">
          <span class="font-weight-light"> Пошук</span>
        </v-btn>
        <v-btn
          border
          color="white"
          prepend-icon="mdi-restore"
          @click="resetAttributeForm">
          <span class="font-weight-light"> Очистити форму</span>
        </v-btn>
      </div>

      <v-card
        border
        class="mt-8 border-dashed bg-grey-lighten-5"
        variant="flat">
        <v-card-text>
          <div class="d-flex flex-column ga-1 mb-4">
            <div class="d-flex align-center ga-1">
              <v-icon
                color="secondary"
                icon="mdi-bookmark-outline"
                size="small"></v-icon>
              <span class="font-size-13 font-weight-bold">
                Зберегти конфігурацію пошуку
              </span>
            </div>
            <div class="font-size-11 text-disabled">
              Спочатку заповніть бажані критерії пошуку, після чого зможете
              зберегти їх як іменовану конфігурацію.
            </div>
          </div>

          <v-row>
            <v-col cols="12" md="10">
              <b-input
                v-model="attributeConfigName"
                :readonly="!canSaveAttributeConfig"
                hide-details
                placeholder="Введіть назву шаблону..."></b-input>
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                :disabled="
                  !canSaveAttributeConfig || !attributeConfigName.trim()
                "
                block
                border
                color="white"
                height="40"
                prepend-icon="mdi-content-save-outline"
                @click="saveCurrentConfig">
                <span class="font-size-13 font-weight-light">Зберегти</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { getDirectory } from '@/directory'
import {
  BCheckbox,
  BInput,
  BInputDate,
  BInputNumber,
  BSelect,
  BTextarea,
} from 'best-modules/components'
import { useSearch } from '@/pages/search/search'
import type { DocumentTypeDefinition } from '@/utils/types/documentType'
import type {
  DocumentTypeField,
  DocumentTypeFieldDefinition,
} from '@/utils/types/documentTypeField'

defineOptions({
  name: 'AttributeSearchForm',
})

const route = useRoute()
const fieldMenu = ref(false)
const {
  attributeForm: form,
  attributeConfigName,
  availableAttributeFields,
  availableRegistrationJournals,
  canSaveAttributeConfig,
  fillSearchDictionaries,
  addAttributeField,
  setAttributeDocTypeDefinition,
  removeAttributeField,
  resetAttributeForm,
  getAttributeConfigsByClass,
  saveAttributeConfig,
  applyAttributeConfig,
  removeAttributeConfig,
  search,
} = useSearch()

type AvailableField = DocumentTypeField | DocumentTypeFieldDefinition

const classColors = [
  'secondary',
  'deep-purple-accent-3',
  'warning',
  'success',
  'error',
  'blue-darken-3',
  'deep-purple-accent-3',
  'warning',
]

const classIcons = [
  'mdi-file-document-outline',
  'mdi-archive-outline',
  'mdi-check-decagram-outline',
  'mdi-message-outline',
  'mdi-file-document-alert-outline',
  'mdi-calendar-blank-outline',
  'mdi-book-open-page-variant-outline',
  'mdi-account-key-outline',
]

const selectedClassId = computed(() => Number(route.params.classKey))

const selectedClass = computed(() => {
  return getDirectory('docTypeClasses').find(
    item => item.id === selectedClassId.value
  )
})

const selectedClassIndex = computed(() => {
  return Math.max(
    getDirectory('docTypeClasses').findIndex(
      item => item.id === selectedClassId.value
    ),
    0
  )
})

const docTypeDefinitions = computed(() => {
  return (
    getDirectory('docTypeDefinitions') as DocumentTypeDefinition[]
  ).filter(item => item.docTypeClassId === selectedClassId.value)
})

const savedConfigs = computed(() => {
  return getAttributeConfigsByClass(selectedClassId.value)
})

const docTypeDefinitionId = computed({
  get: () => form.docTypeDefinitionId,
  set: value => setAttributeDocTypeDefinition(value),
})

onMounted(() => {
  fillSearchDictionaries()
})

const getAvailableFieldKey = (field: AvailableField) => {
  return 'fieldType' in field ? field.id : field.fieldDefinitionId
}

const getAvailableFieldLabel = (field: AvailableField) => {
  return 'fieldType' in field
    ? field.name
    : field.label || field.fieldDefinition.name
}

const getAvailableFieldType = (field: AvailableField) => {
  return 'fieldType' in field
    ? field.fieldType
    : field.fieldDefinition.fieldType
}

const handleAddField = (field: AvailableField) => {
  addAttributeField(field)
  fieldMenu.value = false
}

const isSelectField = (fieldType: string) => {
  return ['select', 'multiselect'].includes(fieldType)
}

const selectedClassColor = computed(() => {
  return classColors[selectedClassIndex.value % classColors.length]
})

const selectedClassIcon = computed(() => {
  return classIcons[selectedClassIndex.value % classIcons.length]
})

const getFieldTypeLabel = (fieldType: string) => {
  const labels: Record<string, string> = {
    string: 'Текст',
    text: 'Текст',
    integer: 'Число',
    decimal: 'Число',
    date: 'Дата',
    boolean: 'Так/Ні',
    select: 'Список',
    multiselect: 'Список',
  }

  return labels[fieldType] || fieldType
}

const getFieldTypeColor = (fieldType: string) => {
  switch (fieldType) {
    case 'date':
      return 'orange-lighten-5'

    case 'select':
    case 'multiselect':
      return 'deep-purple-lighten-5'

    case 'integer':
    case 'decimal':
      return 'green-lighten-5'

    case 'boolean':
      return 'red-lighten-5'

    default:
      return 'blue-lighten-5'
  }
}

const getFieldTypeTextClass = (fieldType: string) => {
  switch (fieldType) {
    case 'date':
      return 'text-orange-darken-2'

    case 'select':
    case 'multiselect':
      return 'text-deep-purple-accent-3'

    case 'integer':
    case 'decimal':
      return 'text-green-darken-2'

    case 'boolean':
      return 'text-red-darken-2'

    default:
      return 'text-secondary'
  }
}

const getFieldPlaceholder = (label: string) => {
  return `Введіть ${label.toLowerCase()}`
}

const handleSearch = () => {
  form.docTypeClassId = selectedClassId.value
  search('attribute', selectedClass.value?.name || 'Атрибутивний пошук')
}

const saveCurrentConfig = () => {
  saveAttributeConfig(selectedClassId.value)
}
</script>
